import { COIN_PER_Q } from '../ads/adConfig.js';

// AdSense-safe general-knowledge question pool (factual, family-friendly).
// Each play of a quiz draws a ROTATING slice of QUESTION_COUNT questions so the
// same quiz feels fresh every time. Order and answer positions are also shuffled.
export const QUESTION_COUNT = 5;

const POOL = [
  { prompt: 'What is the largest planet in our solar system?', options: ['Saturn', 'Jupiter', 'Neptune'], answerIndex: 1 },
  { prompt: 'Which gas do plants absorb from the air?', options: ['Oxygen', 'Carbon dioxide', 'Hydrogen'], answerIndex: 1 },
  { prompt: 'How many continents are there on Earth?', options: ['Five', 'Six', 'Seven'], answerIndex: 2 },
  { prompt: 'What is the capital of France?', options: ['Rome', 'Paris', 'Madrid'], answerIndex: 1 },
  { prompt: 'Which is the tallest mountain on Earth?', options: ['K2', 'Mount Everest', 'Kilimanjaro'], answerIndex: 1 },
  { prompt: 'What is 15 × 4?', options: ['45', '60', '75'], answerIndex: 1 },
  { prompt: 'Who wrote the play "Romeo and Juliet"?', options: ['Shakespeare', 'Dickens', 'Tolstoy'], answerIndex: 0 },
  { prompt: 'Which planet is known as the Red Planet?', options: ['Venus', 'Mars', 'Jupiter'], answerIndex: 1 },
  { prompt: 'What is the largest ocean on Earth?', options: ['Atlantic', 'Indian', 'Pacific'], answerIndex: 2 },
  { prompt: 'How many legs does a spider have?', options: ['Six', 'Eight', 'Ten'], answerIndex: 1 },
  { prompt: 'What is the freezing point of water in Celsius?', options: ['0°', '10°', '32°'], answerIndex: 0 },
  { prompt: 'Which animal is called the King of the Jungle?', options: ['Tiger', 'Lion', 'Elephant'], answerIndex: 1 },
  { prompt: 'What is the capital of Japan?', options: ['Seoul', 'Tokyo', 'Beijing'], answerIndex: 1 },
  { prompt: 'Which metal is liquid at room temperature?', options: ['Iron', 'Mercury', 'Copper'], answerIndex: 1 },
  { prompt: 'How many colours are there in a rainbow?', options: ['Five', 'Six', 'Seven'], answerIndex: 2 },
  { prompt: 'What is the chemical symbol for gold?', options: ['Go', 'Au', 'Gd'], answerIndex: 1 },
  { prompt: 'What is the smallest prime number?', options: ['0', '1', '2'], answerIndex: 2 },
  { prompt: 'What do bees collect from flowers?', options: ['Water', 'Nectar', 'Sand'], answerIndex: 1 },
  { prompt: 'Which planet is closest to the Sun?', options: ['Mercury', 'Venus', 'Earth'], answerIndex: 0 },
  { prompt: 'What is the main language spoken in Brazil?', options: ['Spanish', 'Portuguese', 'French'], answerIndex: 1 },
  { prompt: 'How many sides does a hexagon have?', options: ['Five', 'Six', 'Seven'], answerIndex: 1 },
  { prompt: 'Which organ pumps blood in the human body?', options: ['Lungs', 'Heart', 'Liver'], answerIndex: 1 },
  { prompt: 'What is the largest mammal in the world?', options: ['Elephant', 'Blue whale', 'Giraffe'], answerIndex: 1 },
  { prompt: 'Which country is home to the kangaroo?', options: ['India', 'Australia', 'Canada'], answerIndex: 1 },
  { prompt: 'What is the main ingredient in bread?', options: ['Rice', 'Flour', 'Sugar'], answerIndex: 1 },
  { prompt: 'How many days are there in a leap year?', options: ['364', '365', '366'], answerIndex: 2 },
  { prompt: 'Which planet has the most famous rings?', options: ['Mars', 'Saturn', 'Venus'], answerIndex: 1 },
  { prompt: 'What is the hardest natural substance?', options: ['Gold', 'Diamond', 'Iron'], answerIndex: 1 },
  { prompt: 'Which bird is famous for its colourful tail feathers?', options: ['Crow', 'Peacock', 'Sparrow'], answerIndex: 1 },
  { prompt: 'What is the capital of Italy?', options: ['Rome', 'Athens', 'Lisbon'], answerIndex: 0 },
  { prompt: 'Which season comes right after winter?', options: ['Summer', 'Spring', 'Autumn'], answerIndex: 1 },
  { prompt: 'How many minutes are there in one hour?', options: ['30', '60', '100'], answerIndex: 1 },
];

const shuffle = (a) => {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Category-specific question banks (keyed by the category slug id). Categories
// listed here get their OWN topical questions; everything else falls back to the
// general POOL above. Add more banks over time to keep each category unique.
const BANKS = {
  science: [
    { prompt: 'What is the chemical symbol for water?', options: ['H2O', 'CO2', 'O2'], answerIndex: 0 },
    { prompt: 'What is the centre of an atom called?', options: ['Nucleus', 'Electron', 'Proton'], answerIndex: 0 },
    { prompt: 'What force pulls objects toward the Earth?', options: ['Magnetism', 'Gravity', 'Friction'], answerIndex: 1 },
    { prompt: 'At sea level, water boils at what temperature?', options: ['50°C', '100°C', '150°C'], answerIndex: 1 },
    { prompt: 'Which gas do humans need to breathe in to live?', options: ['Oxygen', 'Helium', 'Nitrogen'], answerIndex: 0 },
    { prompt: 'What is the powerhouse of the cell?', options: ['Nucleus', 'Mitochondria', 'Ribosome'], answerIndex: 1 },
    { prompt: 'Which scientist proposed the theory of gravity?', options: ['Newton', 'Einstein', 'Darwin'], answerIndex: 0 },
    { prompt: 'What state of matter is ice?', options: ['Solid', 'Liquid', 'Gas'], answerIndex: 0 },
  ],
  'world-history': [
    { prompt: 'The Great Pyramid of Giza was built in which country?', options: ['Greece', 'Egypt', 'Italy'], answerIndex: 1 },
    { prompt: 'Who was the first President of the United States?', options: ['Lincoln', 'Washington', 'Jefferson'], answerIndex: 1 },
    { prompt: 'The Roman Empire was centred on which city?', options: ['Athens', 'Rome', 'Cairo'], answerIndex: 1 },
    { prompt: 'Which ship famously sank in 1912?', options: ['Titanic', 'Mayflower', 'Endeavour'], answerIndex: 0 },
    { prompt: 'Who was a famous queen of ancient Egypt?', options: ['Cleopatra', 'Victoria', 'Joan'], answerIndex: 0 },
    { prompt: 'The Renaissance began in which country?', options: ['France', 'Italy', 'Spain'], answerIndex: 1 },
    { prompt: 'The ancient Olympic Games began in which country?', options: ['Greece', 'Rome', 'Egypt'], answerIndex: 0 },
    { prompt: 'Which wall once divided a famous German city?', options: ['Berlin Wall', 'Great Wall', 'Hadrian’s Wall'], answerIndex: 0 },
  ],
  geography: [
    { prompt: 'What is the largest continent by area?', options: ['Africa', 'Asia', 'Europe'], answerIndex: 1 },
    { prompt: 'Which is the largest ocean?', options: ['Atlantic', 'Pacific', 'Indian'], answerIndex: 1 },
    { prompt: 'What is the capital of Australia?', options: ['Sydney', 'Canberra', 'Melbourne'], answerIndex: 1 },
    { prompt: 'Which is the largest hot desert?', options: ['Gobi', 'Sahara', 'Kalahari'], answerIndex: 1 },
    { prompt: 'The Eiffel Tower is in which city?', options: ['London', 'Paris', 'Rome'], answerIndex: 1 },
    { prompt: 'What is the smallest country in the world?', options: ['Monaco', 'Vatican City', 'Malta'], answerIndex: 1 },
    { prompt: 'Mount Everest sits on the border of Nepal and which country?', options: ['India', 'China', 'Bhutan'], answerIndex: 1 },
    { prompt: 'Which continent is also a country?', options: ['Australia', 'Africa', 'Asia'], answerIndex: 0 },
  ],
  sports: [
    { prompt: 'How many players are on a soccer team on the field?', options: ['9', '11', '13'], answerIndex: 1 },
    { prompt: 'In which sport would you perform a slam dunk?', options: ['Tennis', 'Basketball', 'Cricket'], answerIndex: 1 },
    { prompt: 'How many rings are on the Olympic flag?', options: ['4', '5', '6'], answerIndex: 1 },
    { prompt: 'In tennis, what is a score of zero called?', options: ['Love', 'Nil', 'Duck'], answerIndex: 0 },
    { prompt: 'Which sport uses a shuttlecock?', options: ['Badminton', 'Squash', 'Hockey'], answerIndex: 0 },
    { prompt: 'The Tour de France is a race in which sport?', options: ['Running', 'Cycling', 'Swimming'], answerIndex: 1 },
    { prompt: 'How many players are in a cricket team?', options: ['9', '11', '13'], answerIndex: 1 },
    { prompt: 'In golf, one stroke under par is called a?', options: ['Birdie', 'Eagle', 'Bogey'], answerIndex: 0 },
  ],
  movies: [
    { prompt: 'Which studio created Mickey Mouse?', options: ['Disney', 'Pixar', 'Warner Bros'], answerIndex: 0 },
    { prompt: 'In which series is the line “May the Force be with you”?', options: ['Star Wars', 'Star Trek', 'Avatar'], answerIndex: 0 },
    { prompt: 'What kind of animal is Simba in The Lion King?', options: ['Tiger', 'Lion', 'Leopard'], answerIndex: 1 },
    { prompt: 'Which films feature a young wizard named Harry?', options: ['Harry Potter', 'The Hobbit', 'Narnia'], answerIndex: 0 },
    { prompt: 'What is the name of the cowboy toy in Toy Story?', options: ['Buzz', 'Woody', 'Rex'], answerIndex: 1 },
    { prompt: 'In Finding Nemo, what type of fish is Nemo?', options: ['Clownfish', 'Goldfish', 'Shark'], answerIndex: 0 },
    { prompt: 'Which film is about an ocean liner that sank in 1912?', options: ['Titanic', 'Poseidon', 'Jaws'], answerIndex: 0 },
    { prompt: 'What colour is the ogre Shrek?', options: ['Green', 'Blue', 'Purple'], answerIndex: 0 },
  ],
  music: [
    { prompt: 'How many strings does a standard guitar have?', options: ['4', '6', '8'], answerIndex: 1 },
    { prompt: 'Which instrument has black and white keys?', options: ['Piano', 'Violin', 'Drums'], answerIndex: 0 },
    { prompt: 'How many musicians are in a quartet?', options: ['3', '4', '5'], answerIndex: 1 },
    { prompt: 'A song’s words are called the?', options: ['Lyrics', 'Tempo', 'Rhythm'], answerIndex: 0 },
    { prompt: 'Which instrument do you play by hitting it?', options: ['Flute', 'Drum', 'Harp'], answerIndex: 1 },
    { prompt: '“Do, Re, Mi” are part of a musical what?', options: ['Scale', 'Beat', 'Chord'], answerIndex: 0 },
    { prompt: 'Which brass instrument is small and high-pitched?', options: ['Tuba', 'Trumpet', 'Trombone'], answerIndex: 1 },
    { prompt: 'How many keys are on a standard full piano?', options: ['66', '88', '100'], answerIndex: 1 },
  ],
  animals: [
    { prompt: 'Which is the largest land animal?', options: ['Elephant', 'Rhino', 'Hippo'], answerIndex: 0 },
    { prompt: 'How many legs does an insect have?', options: ['6', '8', '10'], answerIndex: 0 },
    { prompt: 'Which bird cannot fly?', options: ['Penguin', 'Eagle', 'Sparrow'], answerIndex: 0 },
    { prompt: 'What do you call a baby kangaroo?', options: ['Cub', 'Joey', 'Calf'], answerIndex: 1 },
    { prompt: 'What is the fastest land animal?', options: ['Lion', 'Cheetah', 'Horse'], answerIndex: 1 },
    { prompt: 'Which sea creature has eight arms?', options: ['Octopus', 'Crab', 'Jellyfish'], answerIndex: 0 },
    { prompt: 'Which animal is known for changing colour?', options: ['Chameleon', 'Frog', 'Snake'], answerIndex: 0 },
    { prompt: 'Bees produce which sweet food?', options: ['Honey', 'Jam', 'Syrup'], answerIndex: 0 },
  ],
  'space-astronomy': [
    { prompt: 'Which planet is closest to the Sun?', options: ['Mercury', 'Venus', 'Earth'], answerIndex: 0 },
    { prompt: 'What is at the centre of our solar system?', options: ['Earth', 'The Sun', 'The Moon'], answerIndex: 1 },
    { prompt: 'Which planet has the most prominent rings?', options: ['Mars', 'Saturn', 'Venus'], answerIndex: 1 },
    { prompt: 'What is Earth’s only natural satellite?', options: ['The Moon', 'The Sun', 'Mars'], answerIndex: 0 },
    { prompt: 'Which galaxy do we live in?', options: ['Andromeda', 'Milky Way', 'Whirlpool'], answerIndex: 1 },
    { prompt: 'How many planets are in our solar system?', options: ['7', '8', '9'], answerIndex: 1 },
    { prompt: 'Who was the first person to walk on the Moon?', options: ['Neil Armstrong', 'Yuri Gagarin', 'Buzz Aldrin'], answerIndex: 0 },
    { prompt: 'What do we call a star that suddenly explodes?', options: ['Supernova', 'Comet', 'Meteor'], answerIndex: 0 },
  ],
  nature: [
    { prompt: 'Trees give off which gas that we breathe?', options: ['Oxygen', 'Carbon dioxide', 'Nitrogen'], answerIndex: 0 },
    { prompt: 'What do we call frozen rain?', options: ['Hail', 'Mist', 'Dew'], answerIndex: 0 },
    { prompt: 'A young plant grown from a seed is a?', options: ['Seedling', 'Branch', 'Root'], answerIndex: 0 },
    { prompt: 'Bees and butterflies help plants to?', options: ['Pollinate', 'Photograph', 'Pollute'], answerIndex: 0 },
    { prompt: 'What green pigment makes leaves their colour?', options: ['Chlorophyll', 'Keratin', 'Melanin'], answerIndex: 0 },
    { prompt: 'A large area of trees is called a?', options: ['Forest', 'Desert', 'Meadow'], answerIndex: 0 },
    { prompt: 'Which natural event shakes the ground?', options: ['Earthquake', 'Rainbow', 'Sunset'], answerIndex: 0 },
    { prompt: 'What do caterpillars turn into?', options: ['Butterflies', 'Bees', 'Spiders'], answerIndex: 0 },
  ],
  'food-cooking': [
    { prompt: 'Which fruit is yellow and curved?', options: ['Apple', 'Banana', 'Grape'], answerIndex: 1 },
    { prompt: 'What is the main ingredient in bread?', options: ['Flour', 'Sugar', 'Rice'], answerIndex: 0 },
    { prompt: 'Which kitchen tool is used to flip pancakes?', options: ['Spatula', 'Whisk', 'Ladle'], answerIndex: 0 },
    { prompt: 'Churning cream makes which food?', options: ['Butter', 'Cheese', 'Yogurt'], answerIndex: 0 },
    { prompt: 'Sushi originally comes from which country?', options: ['China', 'Japan', 'Thailand'], answerIndex: 1 },
    { prompt: 'Which vegetable can make you cry when you cut it?', options: ['Onion', 'Carrot', 'Potato'], answerIndex: 0 },
    { prompt: 'Food cooked in a dry oven is said to be?', options: ['Baked', 'Boiled', 'Steamed'], answerIndex: 0 },
    { prompt: 'Which red spice is made from dried peppers?', options: ['Paprika', 'Cinnamon', 'Turmeric'], answerIndex: 0 },
  ],
  mathematics: [
    { prompt: 'What is 7 + 8?', options: ['14', '15', '16'], answerIndex: 1 },
    { prompt: 'What is 9 × 6?', options: ['54', '56', '48'], answerIndex: 0 },
    { prompt: 'How many sides does a triangle have?', options: ['3', '4', '5'], answerIndex: 0 },
    { prompt: 'What is half of 50?', options: ['20', '25', '30'], answerIndex: 1 },
    { prompt: 'What is 100 − 37?', options: ['63', '67', '73'], answerIndex: 0 },
    { prompt: 'Which of these is a prime number?', options: ['9', '11', '15'], answerIndex: 1 },
    { prompt: 'How many degrees are in a right angle?', options: ['45', '90', '180'], answerIndex: 1 },
    { prompt: 'What is 12 ÷ 4?', options: ['2', '3', '4'], answerIndex: 1 },
  ],
};

// The question source for a category: its own bank if it has enough questions,
// otherwise the shared general pool.
function poolFor(catId) {
  const bank = BANKS[catId];
  return bank && bank.length >= QUESTION_COUNT ? bank : POOL;
}

const rotKey = (catId) => `qz_rot_${catId || 'default'}`;

function readOffset(catId) {
  const n = Number(localStorage.getItem(rotKey(catId)));
  return Number.isFinite(n) && n >= 0 ? n % poolFor(catId).length : 0;
}

// Bump the per-category rotation pointer so the NEXT play uses different
// questions. Call once when a round starts.
export function advanceRotation(catId) {
  const next = (readOffset(catId) + QUESTION_COUNT) % poolFor(catId).length;
  localStorage.setItem(rotKey(catId), String(next));
}

// Build this round's questions: a rotating slice of the pool, with the question
// order and each question's answer positions shuffled for freshness.
export function getQuizQuestions(catId) {
  const pool = poolFor(catId);
  const offset = readOffset(catId);
  const picked = [];
  for (let i = 0; i < QUESTION_COUNT; i++) picked.push(pool[(offset + i) % pool.length]);
  shuffle(picked);
  return picked.map((q, i) => {
    const tagged = q.options.map((opt, idx) => ({ opt, correct: idx === q.answerIndex }));
    shuffle(tagged);
    return {
      id: i + 1,
      prompt: q.prompt,
      options: tagged.map((t) => t.opt),
      answerIndex: tagged.findIndex((t) => t.correct),
      coins: COIN_PER_Q,
    };
  });
}
